import React, { useState, useEffect, useRef } from 'react';
import { Analytics } from "@vercel/analytics/react"
import { 
  Container, 
  Grid, 
  Card, 
  CardMedia, 
  Typography, 
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  CircularProgress,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Fab,
  Tabs,
  Tab,
  Paper,
  CardContent
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import TwitterIcon from '@mui/icons-material/Twitter';
import FavoriteIcon from '@mui/icons-material/Favorite';
import { QRCodeSVG } from 'qrcode.react';
import QuantumCats from './QuantumCats';

const translations = {
  ru: {
    noAttributes: 'Без атрибутов',
    filters: 'Фильтры',
    searchByName: 'Поиск по имени...',
    showNoAttributes: 'Отображать NFT без атрибутов',
    sort: 'Сортировка',
    byRarity: 'По редкости',
    byName: 'По имени',
    ascending: 'По возрастанию',
    descending: 'По убыванию',
    attributes: 'Атрибуты',
    all: 'Все',
    nftCollection: 'NFT Коллекция',
    inCollection: 'в коллекции',
    noAttributesTitle: 'NFT без атрибутов',
    noAttributesDescription: 'Этот NFT не имеет атрибутов',
    times: 'раз',
    donate: 'Поддержать',
    selectNetwork: 'Выберите сеть',
    copyAddress: 'Копировать адрес',
    addressCopied: 'Адрес скопирован!',
    rank: 'Ранг',
    unique: 'Уникальный',
    resetFilters: 'Сбросить фильтры',
    uniqueDescription: 'Этот NFT уникален и не имеет повторяющихся атрибутов'
  },
  en: {
    noAttributes: 'No Attributes',
    filters: 'Filters',
    searchByName: 'Search by name...',
    showNoAttributes: 'Show NFTs without attributes',
    sort: 'Sort',
    byRarity: 'By rarity',
    byName: 'By name',
    ascending: 'Ascending',
    descending: 'Descending',
    attributes: 'Attributes',
    all: 'All',
    nftCollection: 'NFT Collection',
    inCollection: 'in collection',
    noAttributesTitle: 'NFT without attributes',
    noAttributesDescription: 'This NFT has no attributes',
    times: 'times',
    donate: 'Donate',
    selectNetwork: 'Select network',
    copyAddress: 'Copy address',
    addressCopied: 'Address copied!',
    rank: 'Rank',
    unique: 'Unique',
    resetFilters: 'Reset Filters',
    uniqueDescription: 'This NFT is unique and has no repeating attributes'
  }
};

const walletAddresses = {
  btc: 'bc1pdr3ja49trj82e6lukve66vl6hkp5jwt3z0kl2vn33rckmkv75xqqxvxnh2',
  evm: '0x2bdFFef781bE35aa4E2FFf33Da44429B5865685A',
  sol: 'AffW3DyXXj4fW6uPrxdLcpHZ9Zq5V6mFP6DChXiiUyQi'
};

function App() {
  const [nfts, setNfts] = useState([]);
  const [filteredNfts, setFilteredNfts] = useState([]);
  const [selectedNft, setSelectedNft] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('rarity');
  const [sortDirection, setSortDirection] = useState('desc');
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 });
  const observer = useRef();
  const lastNftRef = useRef();
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [availableAttributes, setAvailableAttributes] = useState([]);
  const [showNoAttributes, setShowNoAttributes] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [attributeFrequency, setAttributeFrequency] = useState({});
  const [language, setLanguage] = useState('en');
  const t = translations[language];
  const [donateOpen, setDonateOpen] = useState(false);
  const [selectedNetwork, setSelectedNetwork] = useState('btc');
  const [copySuccess, setCopySuccess] = useState(false);
  const [currentCollection, setCurrentCollection] = useState('taproot');
  const [selectedAttribute, setSelectedAttribute] = useState('');
  const [quantumCatsData, setQuantumCatsData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Загружаем данные для Taproot Wizards
        const response = await fetch('/result.json');
        const text = await response.text();
        const nftArray = text
          .split('\n')
          .filter(line => line.trim())
          .map(line => {
            try {
              let validJson = line
                .replace(/'([^']*)':/g, '"$1":')
                .replace(/: '([^']*)'/g, ': "$1"')
                .replace(/"([^"]*)"([^"]*)"([^"]*)"/g, (match, p1, p2, p3) => {
                  if (p2 === "'") {
                    return `"${p1}'${p3}"`;
                  }
                  return match;
                });

              const parsed = JSON.parse(validJson);
              return parsed;
            } catch (e) {
              console.error('Error parsing line:', line);
              return null;
            }
          })
          .filter(nft => nft && nft.id && nft.displayName && nft.contentURI);

        // Загружаем данные для Quantum Cats
        const catsResponse = await fetch('/resultQCats_fixed.json');
        const catsData = await catsResponse.json();
        if (!Array.isArray(catsData) || catsData.length === 0) {
          throw new Error('Invalid or empty quantum cats data');
        }
        setQuantumCatsData(catsData);

        // Подсчитываем частоту каждого значения атрибута для Taproot Wizards
        const frequency = {};
        const availableAttrs = {};
        nftArray.forEach(nft => {
          if (nft.attributes && nft.attributes.length > 0) {
            nft.attributes.forEach(attr => {
              const key = `${attr.trait_type}:${attr.value}`;
              frequency[key] = (frequency[key] || 0) + 1;
              
              if (!availableAttrs[attr.trait_type]) {
                availableAttrs[attr.trait_type] = new Set();
              }
              availableAttrs[attr.trait_type].add(attr.value);
            });
          }
        });

        setAttributeFrequency(frequency);

        // Преобразуем Set в массивы для доступных атрибутов
        const formattedAvailableAttrs = {};
        Object.keys(availableAttrs).forEach(key => {
          formattedAvailableAttrs[key] = Array.from(availableAttrs[key]).sort();
        });
        setAvailableAttributes(formattedAvailableAttrs);

        // Вычисляем редкость только для NFT с атрибутами
        nftArray.forEach(nft => {
          if (nft.attributes && nft.attributes.length > 0) {
            const rarityScores = nft.attributes.map(attr => {
              const key = `${attr.trait_type}:${attr.value}`;
              const attrFrequency = frequency[key];
              const totalNfts = nftArray.filter(n => n.attributes && n.attributes.length > 0).length;
              return 1 - (attrFrequency / totalNfts);
            });

            nft.rarityScore = rarityScores.reduce((a, b) => a + b, 0) / rarityScores.length;
          } else {
            nft.rarityScore = 0;
          }
        });

        // Сортируем по редкости только NFT с атрибутами
        const nftsWithAttributes = nftArray.filter(nft => nft.attributes && nft.attributes.length > 0);
        const sortedNftsWithAttributes = [...nftsWithAttributes].sort((a, b) => b.rarityScore - a.rarityScore);
        
        // Добавляем NFT без атрибутов в начало списка
        const nftsWithoutAttributes = nftArray.filter(nft => !nft.attributes || nft.attributes.length === 0);
        const allNfts = [
          ...nftsWithoutAttributes.map(nft => ({ ...nft, rank: 1, isUnique: true })),
          ...sortedNftsWithAttributes.map((nft, index) => ({ ...nft, rank: index + 1 }))
        ];
        
        setNfts(allNfts);
        setFilteredNfts(allNfts);
      } catch (error) {
        console.error('Error loading data:', error);
        setError('Ошибка загрузки данных. Пожалуйста, обновите страницу.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  useEffect(() => {
    let filtered = [...nfts];

    // Фильтрация по поиску
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(nft => 
        nft.displayName.toLowerCase().includes(query)
      );
    }

    // Фильтрация по атрибутам
    if (Object.keys(selectedAttributes).length > 0) {
      filtered = filtered.filter(nft => {
        return Object.entries(selectedAttributes).every(([trait, value]) => {
          if (!value || value === "Все") return true;
          return nft.attributes && nft.attributes.some(attr => 
            attr.trait_type === trait && attr.value === value
          );
        });
      });
    }

    // Сортировка
    if (sortBy === 'name') {
      filtered.sort((a, b) => {
        const numA = parseInt(a.displayName.match(/#(\d+)/)[1]);
        const numB = parseInt(b.displayName.match(/#(\d+)/)[1]);
        return sortDirection === 'asc' ? numA - numB : numB - numA;
      });
    } else {
      // Разделяем на NFT с атрибутами и без
      const nftsWithAttributes = filtered.filter(nft => nft.attributes && nft.attributes.length > 0);
      const nftsWithoutAttributes = filtered.filter(nft => !nft.attributes || nft.attributes.length === 0);

      // Сортируем NFT с атрибутами
      const sortedNftsWithAttributes = [...nftsWithAttributes].sort((a, b) => {
        if (sortBy === 'rarity') {
          return sortDirection === 'asc' 
            ? a.rarityScore - b.rarityScore 
            : b.rarityScore - a.rarityScore;
        }
        return 0;
      });

      // Сортируем NFT без атрибутов по имени
      const sortedNftsWithoutAttributes = [...nftsWithoutAttributes].sort((a, b) => {
        const numA = parseInt(a.displayName.match(/#(\d+)/)[1]);
        const numB = parseInt(b.displayName.match(/#(\d+)/)[1]);
        return sortDirection === 'asc' 
          ? numA - numB
          : numB - numA;
      });

      // Объединяем списки в зависимости от типа сортировки
      filtered = sortBy === 'rarity' && sortDirection === 'asc'
        ? [...sortedNftsWithAttributes, ...sortedNftsWithoutAttributes]
        : sortBy === 'rarity' && sortDirection === 'desc'
          ? [...sortedNftsWithoutAttributes, ...sortedNftsWithAttributes]
          : [...sortedNftsWithAttributes, ...sortedNftsWithoutAttributes].sort((a, b) => {
              const numA = parseInt(a.displayName.match(/#(\d+)/)[1]);
              const numB = parseInt(b.displayName.match(/#(\d+)/)[1]);
              return sortDirection === 'asc' 
                ? numA - numB
                : numB - numA;
            });
    }

    setFilteredNfts(filtered);
    setVisibleRange({ start: 0, end: 20 });
  }, [searchQuery, nfts, sortBy, sortDirection, selectedAttributes]);

  useEffect(() => {
    // Настройка Intersection Observer для ленивой загрузки
    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && visibleRange.end < filteredNfts.length) {
          setVisibleRange(prev => ({
            start: prev.start,
            end: Math.min(prev.end + 20, filteredNfts.length)
          }));
        }
      },
      { threshold: 0.1 }
    );

    if (lastNftRef.current) {
      observer.current.observe(lastNftRef.current);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [visibleRange, filteredNfts.length]);

  const handleImageError = (nftId) => {
    setImageErrors(prev => ({
      ...prev,
      [nftId]: true
    }));
  };

  const handleClickOpen = (nft) => {
    setSelectedNft(nft);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedNft(null);
  };

  const handleDonateOpen = () => {
    setDonateOpen(true);
  };

  const handleDonateClose = () => {
    setDonateOpen(false);
  };

  const handleCopyAddress = async () => {
    try {
      await navigator.clipboard.writeText(walletAddresses[selectedNetwork]);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  const handleCollectionChange = (collection) => {
    setCurrentCollection(collection);
    setSearchQuery('');
    setSelectedAttribute('');
    setSelectedAttributes({});
    setVisibleRange({ start: 0, end: 50 });
    window.scrollTo(0, 0);

    // Обновляем доступные атрибуты в зависимости от коллекции
    if (collection === 'taproot') {
      const availableAttrs = {};
      nfts.forEach(nft => {
        if (nft.attributes && nft.attributes.length > 0) {
          nft.attributes.forEach(attr => {
            if (!availableAttrs[attr.trait_type]) {
              availableAttrs[attr.trait_type] = new Set();
            }
            availableAttrs[attr.trait_type].add(attr.value);
          });
        }
      });

      const formattedAttributes = {};
      Object.keys(availableAttrs).forEach(key => {
        formattedAttributes[key] = Array.from(availableAttrs[key]).sort();
      });
      setAvailableAttributes(formattedAttributes);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ 
        display: 'flex', 
        gap: 4,
        backgroundColor: 'transparent',
        borderRadius: 2,
        p: { xs: 1, sm: 2, md: 3 },
        flexDirection: { xs: 'column', md: 'row' }
      }}>
        {/* Левая панель с фильтрами */}
        <Box sx={{ 
          width: { xs: '100%', md: 300 }, 
          flexShrink: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: 2,
          p: { xs: 1, sm: 2 },
          border: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          position: { xs: 'relative', md: 'sticky' },
          top: { xs: 0, md: 20 },
          height: { xs: 'auto', md: 'calc(100vh - 40px)' },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mb: { xs: 2, md: 0 }
        }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            flexWrap: 'wrap'
          }}>
            <Button
              variant="outlined"
              startIcon={<FavoriteIcon />}
              onClick={handleDonateOpen}
              sx={{
                color: 'primary.main',
                borderColor: 'primary.main',
                flex: 1,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white'
                }
              }}
            >
              {t.donate}
            </Button>
            <Button
              variant="outlined"
              onClick={() => setLanguage(prev => prev === 'ru' ? 'en' : 'ru')}
              sx={{
                color: 'primary.main',
                borderColor: 'primary.main',
                flex: 1,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white'
                }
              }}
            >
              {language === 'ru' ? 'EN' : 'RU'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<TwitterIcon />}
              href="https://x.com/eazer_software"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                borderColor: 'primary.main',
                flex: 1,
                minWidth: 'auto',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white'
                }
              }}
            >
              X.com
            </Button>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            gap: 1,
            mb: 2
          }}>
            <Button
              variant="contained"
              onClick={() => handleCollectionChange('taproot')}
              sx={{ 
                flex: 1,
                backgroundColor: currentCollection === 'taproot' ? 'primary.main' : 'rgba(255, 255, 255, 0.05)',
                color: currentCollection === 'taproot' ? 'white' : 'primary.main',
                '&:hover': {
                  backgroundColor: currentCollection === 'taproot' ? 'primary.dark' : 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Taproot Wizard
            </Button>
            <Button
              variant="contained"
              onClick={() => handleCollectionChange('quantum')}
              sx={{
                flex: 1,
                backgroundColor: currentCollection === 'quantum' ? 'primary.main' : 'rgba(255, 255, 255, 0.05)',
                color: currentCollection === 'quantum' ? 'white' : 'primary.main',
                '&:hover': {
                  backgroundColor: currentCollection === 'quantum' ? 'primary.dark' : 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              Quantum Cats
            </Button>
          </Box>

          <Typography 
            variant="h6" 
            gutterBottom
            sx={{ 
              fontWeight: 600,
              color: 'primary.main',
              mb: 3,
              mt: 2
            }}
          >
            {t.filters}
          </Typography>
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={t.searchByName}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'grey.500' }} />,
                sx: {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  },
                  color: 'white'
                }
              }}
            />
          </Box>
          <Box sx={{ mb: 3 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>{t.sort}</InputLabel>
              <Select
                value={sortBy}
                label={t.sort}
                onChange={(e) => setSortBy(e.target.value)}
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'primary.main'
                  },
                  color: 'white'
                }}
              >
                <MenuItem value="rarity">{t.byRarity}</MenuItem>
                <MenuItem value="name">{t.byName}</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              mt: 1
            }}>
              <Button 
                onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                variant="outlined"
                size="small"
                fullWidth
                sx={{ 
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  height: 32,
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    {sortDirection === 'asc' ? t.ascending : t.descending}
                  </Typography>
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </Box>
              </Button>
            </Box>
          </Box>
          <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
            <Typography 
              variant="subtitle1" 
              gutterBottom
              sx={{ 
                fontWeight: 500,
                color: 'white',
                mb: 1
              }}
            >
              {t.attributes}
            </Typography>
            {Object.entries(availableAttributes).map(([trait, values]) => (
              <Box key={trait} sx={{ mb: 1 }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    mb: 0.5,
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.875rem'
                  }}
                >
                  {trait}
                </Typography>
                <Select
                  fullWidth
                  size="small"
                  value={selectedAttributes[trait] || ''}
                  onChange={(e) => setSelectedAttributes(prev => ({
                    ...prev,
                    [trait]: e.target.value
                  }))}
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.2)'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'rgba(255, 255, 255, 0.3)'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main'
                    },
                    color: 'white',
                    height: '32px'
                  }}
                >
                  <MenuItem value="">{t.all}</MenuItem>
                  {values.map(value => (
                    <MenuItem key={value} value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            ))}
          </Box>
          <Button
            variant="outlined"
            onClick={() => setSelectedAttributes({})}
            sx={{
              color: 'primary.main',
              borderColor: 'primary.main',
              mt: 2,
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'white'
              }
            }}
          >
            {t.resetFilters}
          </Button>
        </Box>

        {/* Основной контент */}
        <Box sx={{ flexGrow: 1 }}>
          {currentCollection === 'taproot' ? (
            <Grid container spacing={{ xs: 1, sm: 2 }}>
              {filteredNfts.slice(visibleRange.start, visibleRange.end).map((nft, index) => (
                <Grid item xs={6} sm={4} md={3} lg={2} key={nft.id} ref={index === visibleRange.end - 1 ? lastNftRef : null}>
                  <Card 
                    onClick={() => handleClickOpen(nft)}
                    sx={{ 
                      cursor: 'pointer',
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: nft.isUnique 
                        ? 'rgba(255, 215, 0, 0.5)' 
                        : 'rgba(255, 255, 255, 0.1)',
                      transition: 'all 0.3s ease',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      '&:hover': {
                        transform: 'translateY(-5px)',
                        boxShadow: nft.isUnique 
                          ? '0 8px 24px rgba(255, 215, 0, 0.2)' 
                          : '0 8px 24px rgba(0, 0, 0, 0.2)',
                        borderColor: nft.isUnique 
                          ? 'rgba(255, 215, 0, 0.8)' 
                          : 'rgba(255, 255, 255, 0.2)'
                      }
                    }}
                  >
                    <Box sx={{ position: 'relative', paddingTop: '100%' }}>
                      {imageErrors[nft.id] ? (
                        <Box
                          position="absolute"
                          top={0}
                          left={0}
                          right={0}
                          bottom={0}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '16px 16px 0 0'
                          }}
                        >
                          <ImageNotSupportedIcon sx={{ fontSize: 40, color: 'rgba(255, 255, 255, 0.3)' }} />
                        </Box>
                      ) : (
                        <CardMedia
                          component="img"
                          image={nft.contentURI}
                          alt={nft.displayName}
                          onError={() => handleImageError(nft.id)}
                          sx={{ 
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '16px 16px 0 0'
                          }}
                        />
                      )}
                    </Box>
                    <CardContent sx={{ p: 1.5, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography 
                        variant="subtitle2"
                        sx={{ 
                          color: 'white',
                          fontWeight: 500,
                          mb: 0.5
                        }}
                      >
                        {nft.displayName}
                      </Typography>
                      <Box sx={{ mt: 'auto' }}>
                        <Typography 
                          variant="caption"
                          sx={{ 
                            color: nft.isUnique 
                              ? 'rgba(255, 215, 0, 0.8)' 
                              : 'rgba(255, 255, 255, 0.7)',
                            display: 'block',
                            mb: 0.5,
                            fontWeight: nft.isUnique ? 600 : 400
                          }}
                        >
                          {t.rank}: {nft.isUnique ? t.unique : `#${nft.rank}`}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <QuantumCats 
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              sortBy={sortBy}
              setSortBy={setSortBy}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
              showNoAttributes={showNoAttributes}
              setShowNoAttributes={setShowNoAttributes}
              selectedAttribute={selectedAttribute}
              setSelectedAttribute={setSelectedAttribute}
              selectedAttributes={selectedAttributes}
              setSelectedAttributes={setSelectedAttributes}
              language={language}
              quantumCatsData={quantumCatsData}
              onNftsLoaded={(nfts) => {
                const attributes = {};
                nfts.forEach(nft => {
                  if (nft.attributes && nft.attributes.length > 0) {
                    nft.attributes.forEach(attr => {
                      if (!attributes[attr.trait_type]) {
                        attributes[attr.trait_type] = new Set();
                      }
                      attributes[attr.trait_type].add(attr.value);
                    });
                  }
                });

                const formattedAttributes = {};
                Object.keys(attributes).forEach(key => {
                  formattedAttributes[key] = Array.from(attributes[key]).sort();
                });
                setAvailableAttributes(formattedAttributes);
                setSelectedAttribute('');
              }}
            />
          )}
        </Box>
      </Box>

      {showScrollTop && (
        <Fab
          color="primary"
          onClick={scrollToTop}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            border: '1px solid',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.2)'
            }
          }}
        >
          <KeyboardArrowUpIcon />
        </Fab>
      )}

      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: 'rgba(18, 18, 18, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            m: { xs: 1, sm: 2 },
            maxHeight: { xs: '90vh', sm: '95vh' },
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          pb: 2
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Typography 
                variant="h6"
                sx={{ 
                  fontWeight: 600,
                  color: 'white'
                }}
              >
                {selectedNft?.displayName}
              </Typography>
              <Typography 
                variant="subtitle1"
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.7)',
                  mt: 0.5
                }}
              >
                {selectedNft?.isUnique ? t.unique : `#${selectedNft?.rank}`} {t.inCollection}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Button
                variant="outlined"
                size="small"
                href={`https://magiceden.io/ordinals/item-details/${selectedNft?.id}`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'primary.main',
                  borderColor: 'primary.main',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                    color: 'white'
                  }
                }}
              >
                MagicEden
              </Button>
              <IconButton 
                onClick={handleClose}
                sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)'
                  }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedNft && (
            <Grid container spacing={{ xs: 2, sm: 4 }}>
              {/* Левая колонка с изображением */}
              <Grid item xs={12} md={6}>
                {imageErrors[selectedNft.id] ? (
                  <Box
                    height={{ xs: 300, sm: 400, md: 600 }}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ 
                      borderRadius: 2,
                      backgroundColor: 'rgba(255, 255, 255, 0.05)'
                    }}
                  >
                    <ImageNotSupportedIcon sx={{ fontSize: { xs: 40, sm: 60, md: 80 }, color: 'rgba(255, 255, 255, 0.3)' }} />
                  </Box>
                ) : (
                  <CardMedia
                    component="img"
                    height={{ xs: 300, sm: 400, md: 600 }}
                    image={selectedNft.contentURI}
                    alt={selectedNft.displayName}
                    onError={() => handleImageError(selectedNft.id)}
                    sx={{ 
                      borderRadius: 2,
                      objectFit: 'contain'
                    }}
                  />
                )}
              </Grid>

              {/* Правая колонка с атрибутами */}
              <Grid item xs={12} md={6}>
                <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Typography 
                    variant="h6" 
                    gutterBottom
                    sx={{ 
                      fontWeight: 600,
                      color: 'white',
                      mb: 3
                    }}
                  >
                    {t.attributes}
                  </Typography>
                  {selectedNft.attributes && selectedNft.attributes.length > 0 ? (
                    <Grid container spacing={2}>
                      {selectedNft.attributes.map((attr, index) => {
                        const key = `${attr.trait_type}:${attr.value}`;
                        const frequency = attributeFrequency[key] || 0;
                        const totalNfts = nfts.filter(n => n.attributes && n.attributes.length > 0).length;
                        const percentage = ((frequency / totalNfts) * 100).toFixed(2);
                        
                        return (
                          <Grid item xs={12} key={index}>
                            <Box
                              sx={{
                                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                borderRadius: 1,
                                p: 2,
                                border: '1px solid',
                                borderColor: 'rgba(255, 255, 255, 0.05)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                                  transform: 'translateY(-2px)',
                                  borderColor: 'rgba(255, 255, 255, 0.1)'
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                <Typography 
                                  variant="subtitle2" 
                                  sx={{ 
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontWeight: 500
                                  }}
                                >
                                  {attr.trait_type}
                                </Typography>
                                <Typography 
                                  variant="caption"
                                  sx={{ 
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                    padding: '4px 8px',
                                    borderRadius: '12px'
                                  }}
                                >
                                  {frequency} {t.times} ({percentage}%)
                                </Typography>
                              </Box>
                              <Typography 
                                sx={{ 
                                  color: 'white',
                                  fontWeight: 500,
                                  fontSize: '1.1rem'
                                }}
                              >
                                {attr.value}
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      })}
                    </Grid>
                  ) : (
                    <Box
                      sx={{
                        backgroundColor: 'rgba(255, 215, 0, 0.05)',
                        borderRadius: 2,
                        p: 3,
                        border: '1px solid',
                        borderColor: 'rgba(255, 215, 0, 0.1)',
                        textAlign: 'center'
                      }}
                    >
                      <Typography 
                        variant="h6"
                        sx={{ 
                          color: 'rgba(255, 215, 0, 0.8)',
                          fontWeight: 600,
                          mb: 1
                        }}
                      >
                        {t.unique}
                      </Typography>
                      <Typography 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.7)'
                        }}
                      >
                        {t.uniqueDescription}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      <Dialog 
        open={donateOpen} 
        onClose={handleDonateClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: 'rgba(18, 18, 18, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            m: { xs: 1, sm: 2 },
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '1px solid',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          pb: 2
        }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography 
              variant="h6"
              sx={{ 
                fontWeight: 600,
                color: 'white'
              }}
            >
              {t.donate}
            </Typography>
            <IconButton 
              onClick={handleDonateClose}
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%', mt: 2 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center',
              mb: 3
            }}>
              <Tabs
                value={selectedNetwork}
                onChange={(e, newValue) => setSelectedNetwork(newValue)}
                sx={{
                  '& .MuiTab-root': {
                    color: 'rgba(255, 255, 255, 0.7)',
                    '&.Mui-selected': {
                      color: 'primary.main'
                    }
                  }
                }}
              >
                <Tab label="BTC" value="btc" />
                <Tab label="EVM" value="evm" />
                <Tab label="SOL" value="sol" />
              </Tabs>
            </Box>
            
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              gap: 2
            }}>
              <Paper 
                sx={{ 
                  p: 3, 
                  backgroundColor: 'white',
                  borderRadius: 2,
                  width: { xs: '100%', sm: 'auto' },
                  maxWidth: '100%',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
                }}
              >
                <QRCodeSVG 
                  value={walletAddresses[selectedNetwork]}
                  size={Math.min(200, window.innerWidth * 0.5)}
                />
              </Paper>
              
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                p: 2,
                borderRadius: 2,
                width: '100%',
                border: '1px solid',
                borderColor: 'rgba(255, 255, 255, 0.05)'
              }}>
                <Typography 
                  sx={{ 
                    color: 'white',
                    fontFamily: 'monospace',
                    fontSize: '0.875rem',
                    textAlign: 'center'
                  }}
                >
                  {walletAddresses[selectedNetwork]}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleCopyAddress}
                  sx={{
                    color: 'primary.main',
                    borderColor: 'primary.main',
                    minWidth: 'auto',
                    px: 2
                  }}
                >
                  {copySuccess ? t.addressCopied : t.copyAddress}
                </Button>
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      {error && (
        <Box sx={{ 
          position: 'fixed', 
          top: 20, 
          right: 20, 
          zIndex: 9999,
          bgcolor: 'error.main',
          color: 'error.contrastText',
          p: 2,
          borderRadius: 1,
          boxShadow: 3
        }}>
          {error}
        </Box>
      )}
      <Analytics />
    </Container>
  );
}

export default App; 